import { useCallback, useEffect, useReducer, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import { errorNormalizer } from "../../../shared/Helpers/functions";
import AssessmentData from "./assessmentData";
import {
  ACTIONS,
  assessmentReducer,
  initialAssessmentState,
} from "./assessmentReducer";
import { useResetCalendarData } from "../../Calendar/Helpers/hooks";
import SoapNoteButtons from "../components/soapNoteButtons";

import { DispatchProperties, useSelector } from "../../../redux/store";
import {
  getSoapNote,
  updateAssessment,
} from "../../../redux/State/clientSlice/soapNoteSlice";
import {
  setDate,
  setEndTime,
  setStartTime,
} from "../../../redux/State/clientSlice/sessionSlice";
import {
  AssessmentProperties,
  UpdateAssessmentProperties,
} from "../../../redux/API/ClientAPIHelpers/soapNoteProperties";

const Assessment = () => {
  const dispatch = useDispatch<DispatchProperties>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { resetData } = useResetCalendarData();

  const forcedOperation =
    params.get("forcedOperation") === "true" ? true : false;
  const reportId = params.get("reportId");
  const clientId = params.get("clientId");
  const sessionId = params.get("sessionId");

  const sessionInfo = useSelector((state) => state.session.sessionInfo);

  const [data, setData] = useReducer(assessmentReducer, initialAssessmentState);
  const currentUpdateRequestRef = useRef<ReturnType<typeof dispatch<any>> | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (currentUpdateRequestRef.current) {
        console.log("Aborting request on unmount");
        currentUpdateRequestRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!reportId || !clientId || !sessionId) return;

    dispatch(getSoapNote({ reportId, clientId }))
      .then(unwrapResult)
      .then((response) => {
        const {
          assessmentToolResponses,
          participantResponses,
          treatmentPlanResponses,
          assessmentActivityResponses,
          location,
          date,
          startTime,
          endTime,
          clientTelehealthLocation,
          userTelehealthLocation,
        } = response as AssessmentProperties;
        const assessmentTools = assessmentToolResponses?.map((x) => x.id);
        const assessmentActivities = assessmentActivityResponses?.map(
          (x) => x.id
        );
        const participants = participantResponses?.map((x) => x.id);
        const treatmentPlans = treatmentPlanResponses?.map((x) => x.id);

        dispatch(setDate(dayjs(date).format()));
        dispatch(setStartTime(dayjs(startTime).format()));
        dispatch(setEndTime(dayjs(endTime).format()));

        setData({
          type: ACTIONS.setState,
          payload: response,
        });

        setData({
          type: ACTIONS.setAssessmentTools,
          payload: assessmentTools,
        });

        setData({
          type: ACTIONS.setAssessmentActivities,
          payload: assessmentActivities,
        });

        setData({
          type: ACTIONS.setParticipants,
          payload: participants,
        });

        setData({
          type: ACTIONS.setTreatmentPlans,
          payload: treatmentPlans,
        });

        if (!!location) {
          setData({
            type: ACTIONS.setLocationId,
            payload: location.id,
          });
        }
        if (!!clientTelehealthLocation) {
          setData({
            type: ACTIONS.setClientTelehealthLocationId,
            payload: clientTelehealthLocation.id,
          });
        }
        if (!!userTelehealthLocation) {
          setData({
            type: ACTIONS.setUserTelehealthLocationId,
            payload: userTelehealthLocation.id,
          });
        }
      })
      .catch(errorNormalizer);
  }, [dispatch, clientId, sessionId, reportId]);

  const updateSoapNoteHandler = useCallback(
    (submit: boolean) => {
      if (currentUpdateRequestRef.current) {
        console.log("Aborting previous pending request");
        currentUpdateRequestRef.current.abort();
      }
      if (!reportId || !clientId || !data) return;
      const { date, startTime, endTime } = sessionInfo;
      const allData: UpdateAssessmentProperties = {
        ...data,
        date,
        startTime,
        endTime,
        reportId,
        clientId,
        submit,
        forcedOperation,
      };
      const requestPromise = dispatch(updateAssessment(allData));
      currentUpdateRequestRef.current = requestPromise;

      requestPromise
        .then(unwrapResult)
        .then(() => {
          if (!submit || !isMounted.current) return;
          resetData();
          toast("Success");
          navigate("/calendar");
        })
        .catch(errorNormalizer)
        .finally(() => {
          if (currentUpdateRequestRef.current === requestPromise) {
            currentUpdateRequestRef.current = null;
          }
        })
    },
    [
      reportId,
      clientId,
      forcedOperation,
      data,
      sessionInfo,
      navigate,
      resetData,
      dispatch,
    ]
  );

  const saveHandler = useCallback(
    () => updateSoapNoteHandler(false),
    [updateSoapNoteHandler]
  );
  const submitHandler = useCallback(
    () => updateSoapNoteHandler(true),
    [updateSoapNoteHandler]
  );

  return (
    <>
      <AssessmentData setData={setData} data={data} save={saveHandler} />
      <SoapNoteButtons save={saveHandler} submit={submitHandler} />
    </>
  );
};

export default Assessment;
